'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { Hourglass, Check, X, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { reviewQueries } from '../services/review.queries';
import type { ReviewMetricTrend } from '../types';

function TrendBadge({ trend }: { trend?: ReviewMetricTrend }) {
  if (!trend) return null;

  return (
    <span
      className={cn(
        'rounded-full px-2.5 py-0.5 text-xs font-medium',
        trend.type === 'positive' &&
          'bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-400',
        trend.type === 'neutral' && 'bg-muted text-muted-foreground',
        trend.type === 'negative' &&
          'bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400'
      )}
    >
      {trend.label}
    </span>
  );
}

export function ReviewMetrics() {
  const t = useTranslations('review');
  const { data, isLoading } = useQuery(reviewQueries.metrics());

  const metrics = data?.data;

  const cards = [
    {
      label: t('metricPending'),
      value: metrics?.pending_count ?? 0,
      icon: Hourglass,
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-50 dark:bg-amber-950',
      trend: metrics?.trends?.pending
    },
    {
      label: t('metricApprovedToday'),
      value: metrics?.approved_today ?? 0,
      icon: Check,
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-950',
      trend: metrics?.trends?.approved
    },
    {
      label: t('metricRejectedToday'),
      value: metrics?.rejected_today ?? 0,
      icon: X,
      color: 'text-rose-600 dark:text-rose-400',
      bgColor: 'bg-rose-50 dark:bg-rose-950',
      trend: metrics?.trends?.rejected
    },
    {
      label: t('metricAvgReviewTime'),
      value: metrics?.avg_review_minutes ?? 0,
      suffix: t('minutes'),
      icon: Clock,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-950',
      trend: metrics?.trends?.avg_time
    }
  ];

  if (isLoading) {
    return (
      <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className='h-32 w-full rounded-lg' />
        ))}
      </div>
    );
  }

  return (
    <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
      {cards.map((card) => (
        <Card key={card.label}>
          <CardContent className='flex flex-col items-start px-5'>
            <div className='flex w-full items-center justify-between'>
              <TrendBadge trend={card.trend} />
              <div className={`rounded-xl p-2.5 ${card.bgColor}`}>
                <card.icon className={`h-5 w-5 ${card.color}`} />
              </div>
            </div>
            <div className='mt-3 text-start'>
              <p className='text-3xl font-bold'>
                {typeof card.value === 'number'
                  ? card.value.toLocaleString()
                  : card.value}
                {'suffix' in card && card.suffix && (
                  <span className='text-muted-foreground ms-1.5 text-base font-medium'>
                    {card.suffix}
                  </span>
                )}
              </p>
              <p className='text-muted-foreground mt-1 text-sm font-medium'>
                {card.label}
              </p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
